import type { Locale } from "@/i18n/routing";
import { formatCurrency } from "@/lib/format-currency";

/**
 * Shape returned by `billing.catalog.getPricingCatalog`, narrowed to the
 * fields the landing surfaces render. Declared structurally (rather than
 * imported via `FunctionReturnType`) so this module stays free of the
 * Convex client and can be used from both a React hook and a server
 * component without pulling either runtime in.
 */
type PackPrice = { cents: number; currency: string };

type PricingCatalog = {
  /**
   * `cents`/`currency` is the Price's own currency (BRL). `prices` lists every
   * currency Stripe charges; optional so a web build that ships ahead of the
   * backend deploy still renders the default price.
   */
  pack: PackPrice & { credits: number; prices?: readonly PackPrice[] };
  grants: {
    anonCredits: number;
    verifiedBonus: number;
    totalFreeCredits: number;
  };
  /** Most credits ONE check can spend: the source page plus a full fan-out (#1767). */
  maxCheckCredits: number;
  /**
   * Item pages one check may fan out to (#2679). Since a following source also
   * reads continuation LIST pages, `maxCheckCredits` stopped being "1 + the
   * item pages" — quoting it at the calculator would credit those pages to the
   * item fan-out the copy is about. Optional so a web build that ships ahead
   * of the backend deploy still renders: without it the old identity holds.
   */
  maxFanOutPages?: number;
  /**
   * Continuation LIST pages a check of a following source reads (#2679).
   * Quoted beside the item pages so the rules copy can name the two costs
   * apart instead of implying one covers the other. Optional for the same
   * reason as `maxFanOutPages`.
   */
  maxContinuationPages?: number;
};

/**
 * Values passed to next-intl's `t(key, values)` for pricing copy. The named
 * keys (`pricePerPack`, `pricePerPackShort`, `costPerCheck`, `creditsPerPack`) document each
 * slot so call sites stay readable; the `string` index signature satisfies
 * next-intl's typed `values: Record<string, string | number | Date>` API
 * without a per-call cast.
 */
type PricingValues = {
  // Every string below renders in the locale's currency (`LOCALE_CURRENCY`):
  // BRL on pt-br, USD on en-US, matching what Stripe charges in each.
  /** Long form: "R$ 50,00" (pt-br) / "$10.00" (en-US) — used inside body copy and calculators. */
  pricePerPack: string;
  /** Short form: "R$ 50" (pt-br) / "$10" (en-US) — used in headlines where sub-unit cents are noise. */
  pricePerPackShort: string;
  /** Per-check rate: "R$ 0,050" (pt-br) / "$0.010" (en-US). */
  costPerCheck: string;
  /** Localized number of credits in the current pack: "1.000" (pt-BR) / "1,000" (en-US). */
  creditsPerPack: string;
  // Free-tier credit grants for "N free credits" copy (issue #1084). Kept as
  // strings so the whole object stays a `Record<string, string>` and the
  // `EMPTY` placeholder set below can zero every slot to "".
  /** Credits an anonymous user starts with ("20"). */
  anonCredits: string;
  /** One-time email-verification bonus ("100"). */
  verifiedBonus: string;
  /** Anon + bonus total ("120") for "N free credits in total" copy. */
  totalFreeCredits: string;
  /**
   * Continuation pages a following source adds to a check ("2"), for the rules
   * copy that quotes them apart from the item pages (#2679). "0" on a backend
   * that does not serve the field yet, which reads as "no extra pages" —
   * exactly the behavior of every source before the policy existed.
   */
  maxContinuationPages: string;
  /**
   * The per-check page ceiling ("6") for the pricing rules copy (#1767). Lives
   * in this bundle, not only as the top-level `maxCheckCredits`, because the
   * rules are rendered through `t(key, pricingValues)` — a slot missing from the
   * bundle renders as a literal `{maxPages}` on the page.
   */
  maxPages: string;
} & Record<string, string>;

/**
 * Static, always-safe empty `PricingValues`. Use this whenever a consumer
 * needs to pass a placeholder set to next-intl but the catalog hasn't
 * resolved yet; every slot is `""` so the `{name}` placeholder syntax
 * never leaks through to the user (review feedback on #1013).
 *
 * `t("foo.bar", EMPTY_PRICING_VALUES)` renders as "foo.bar" with the
 * placeholders silently elided, instead of "foo.bar with {pricePerPack}".
 */
// Frozen so a consumer can't mutate the shared placeholder set and
// contaminate every other pricing caller (review feedback on #1027).
// `Object.freeze` keeps the exported constant referentially stable.
export const EMPTY_PRICING_VALUES: PricingValues = Object.freeze({
  pricePerPack: "",
  pricePerPackShort: "",
  costPerCheck: "",
  creditsPerPack: "",
  anonCredits: "",
  verifiedBonus: "",
  totalFreeCredits: "",
  maxContinuationPages: "",
  maxPages: "",
});

export interface PricingStrings {
  /** Catalog not yet returned by the server (or the read failed). */
  loading: boolean;
  /** Currency the locale quotes and Stripe charges in: "BRL" (pt-br) / "USD" (en-US). */
  currency: string | undefined;
  /** Long form: "R$ 50,00" (pt-br) / "$10.00" (en-US) — used inside body copy and calculators. */
  pricePerPack: string;
  /** Short form: "R$ 50" (pt-br) / "$10" (en-US) — used in headlines where sub-unit cents are noise. */
  pricePerPackShort: string;
  /** Per-check rate: "R$ 0,050" (pt-br) / "$0.010" (en-US). */
  costPerCheck: string;
  /**
   * `{pricePerPack}` placeholder syntax never renders literally.
   */
  pricingValues: PricingValues;
  /** Raw catalog fields for callers that need their own math (e.g. per-URL cost). Undefined while loading. */
  cents: number | undefined;
  credits: number | undefined;
  /**
   * Per-check rate in major units (`cents / credits / 100`). Exposed so the
   * pricing calculator reuses the single derivation instead of repeating the
   * division (review feedback on #1027). Undefined while loading.
   */
  perCheckMajor: number | undefined;
  /**
   * The most credits ONE check can consume — the source page, the continuation
   * pages a following contract reads (#2679) and a full fan-out (#1767).
   * Undefined while loading.
   */
  maxCheckCredits: number | undefined;
  /**
   * Pages a check of a source whose ITEMS have their own pages reads: the
   * source page plus its fan-out. Bounds the calculator's pages-per-check
   * input, which models exactly that source — the continuation pages of a
   * paginated list are not item pages and must not inflate it (#2679).
   * Undefined while loading.
   */
  maxItemPagesPerCheck: number | undefined;
}

/**
 * Safe placeholder returned before the catalog resolves, and by every
 * failure path. Never holds a stale number — a hole is always preferable to
 * a price the checkout will not honor (issue #1012).
 */
export const EMPTY_PRICING_STRINGS: PricingStrings = Object.freeze({
  loading: true,
  currency: undefined,
  pricePerPack: "",
  pricePerPackShort: "",
  costPerCheck: "",
  pricingValues: EMPTY_PRICING_VALUES,
  cents: undefined,
  credits: undefined,
  perCheckMajor: undefined,
  maxCheckCredits: undefined,
  maxItemPagesPerCheck: undefined,
});

/**
 * Turn one catalog reading into the locale-formatted strings every pricing
 * surface renders. Pure: the same catalog and locale always produce the same
 * output, with no Convex or React involved.
 *
 * Shared by `usePricingCatalog` (client components, reactive query) and
 * `getServerPricingStrings` (server components, cached fetch) so a landing
 * rendered on the server and a widget rendered on the client can never format
 * the same catalog differently (issue #1772).
 */
export function derivePricingStrings(
  catalog: PricingCatalog,
  locale: string,
): PricingStrings {
  // The source page plus its item fan-out. Falls back to the old identity
  // (`maxCheckCredits` was exactly that before #2679) for a backend that does
  // not serve `maxFanOutPages` yet.
  const maxItemPagesPerCheck =
    catalog.maxFanOutPages === undefined
      ? catalog.maxCheckCredits
      : 1 + catalog.maxFanOutPages;
  const { credits } = catalog.pack;
  const { cents, currency } = packPriceFor(catalog.pack, locale);
  const { anonCredits, verifiedBonus, totalFreeCredits } = catalog.grants;
  const perCheckMajor = cents / credits / 100;
  const packMajor = cents / 100;
  const pricePerPack = formatCurrency(packMajor, locale, currency, 2);
  const pricePerPackShort = formatCurrency(packMajor, locale, currency, 0);
  const costPerCheck = formatCurrency(perCheckMajor, locale, currency, 3);
  const creditsPerPack = new Intl.NumberFormat(locale).format(credits);
  return {
    loading: false,
    currency,
    pricePerPack,
    pricePerPackShort,
    costPerCheck,
    pricingValues: {
      pricePerPack,
      pricePerPackShort,
      costPerCheck,
      creditsPerPack,
      anonCredits: String(anonCredits),
      verifiedBonus: String(verifiedBonus),
      totalFreeCredits: String(totalFreeCredits),
      maxPages: String(maxItemPagesPerCheck),
      maxContinuationPages: String(catalog.maxContinuationPages ?? 0),
    },
    cents,
    credits,
    perCheckMajor,
    maxCheckCredits: catalog.maxCheckCredits,
    maxItemPagesPerCheck,
  };
}

// The currency each landing locale quotes. Checkout charges a buyer in their
// local currency, so an English reader sees dollars and a Brazilian reader
// sees reais. Keyed by the router's `Locale`, so adding a locale to
// `i18n/routing.ts` without a currency here fails the typecheck instead of
// silently quoting the default price. A catalog without the currency (web
// deployed ahead of the backend) falls back to the pack's default price.
const LOCALE_CURRENCY: Record<Locale, string> = {
  "en-US": "USD",
  "pt-br": "BRL",
};

function packPriceFor(
  pack: PricingCatalog["pack"],
  locale: string,
): PackPrice {
  const currency = LOCALE_CURRENCY[locale as Locale];
  return pack.prices?.find((price) => price.currency === currency) ?? pack;
}
